<template>
  <div class="message-box dialog-mask" @click.self="onClickOutSide">
    <div class="dialog-box">
      <template v-if="isOnlyImage">
        <div class="dialog-image" @click="onPositiveClick">
          <img :src="optimizedImage" loading="lazy" />
        </div>
      </template>
      <template v-else>
        <div class="dialog-header">
          <img :src="optimizedImage" loading="lazy" />
        </div>
        <div class="dialog-body">
          <h1>{{ title }}</h1>
          <div class="text-container">{{ body }}</div>
        </div>
        <div class="button-area">
          <button class="button default" :class="{ single: !enabledNavigationButton }" @click="onClickClose">
            {{ $t("common.close") }}
          </button>
          <button v-if="enabledNavigationButton" class="button primary" @click="onPositiveClick">
            {{ buttonText }}
          </button>
        </div>
      </template>
    </div>
  </div>
</template>

<script lang="ts">
import Vue, { PropType } from "vue";
import { optimizeImage } from "@/lib/utils/url";

export default Vue.extend<Data, Methods, Computed, Props>({
  props: {
    popup: Object as PropType<Popup>,
  },
  computed: {
    id() {
      const { popup } = this;
      const { id } = popup;
      return id;
    },
    title() {
      const { popup } = this;
      const { title } = popup;
      return title;
    },
    body() {
      const { popup } = this;
      const { body } = popup;
      return body;
    },
    image() {
      const { popup } = this;
      const { image } = popup;
      const { url } = image;
      return url;
    },
    optimizedImage() {
      return optimizeImage(this.image);
    },
    buttonText() {
      const { popup } = this;
      const { buttonText } = popup;
      return buttonText;
    },
    url() {
      const { popup } = this;
      const { url } = popup;
      return url;
    },
    page() {
      const { popup } = this;
      const { page } = popup;
      return page && page[0];
    },
    transition() {
      const { popup } = this;
      const { transition } = popup;
      return transition;
    },
    enabledNavigationButton() {
      const { buttonText, url } = this;
      return !!(buttonText && url);
    },
    isOnlyImage() {
      const { title, body, image, buttonText, url } = this;
      return !!(image && url) && !(title && body && buttonText);
    },
  },
  methods: {
    onPositiveClick() {
      const { url, transition } = this;

      if (transition.includes("newTab")) window.open(url);
      else window.location.href = url!;

      this.$close(true);
    },
    onClickOutSide() {
      this.$close(true);
    },
    onClickClose() {
      this.$close(true);
    },
  },
});

interface Props {
  popup: Popup;
}

interface Data {}

interface Computed {
  id: string;
  title: string;
  body: string;
  image: string;
  buttonText?: string;
  url?: string;
  page?: string;
  transition: Transition[];
  enabledNavigationButton: boolean;
  isOnlyImage: boolean;
  optimizedImage: string;
}

interface Methods {
  onPositiveClick: () => void;
  onClickOutSide: () => void;
  onClickClose: () => void;
}

export type PopupDialogProps = Props;

export type Popup = {
  id: string;
  startedAt?: string;
  endedAt?: string;
  title: string;
  body: string;
  image: {
    url: string;
  };
  buttonText?: string;
  url?: string;
  platform: Platform[];
  version?: string;
  target: Target[];
  page: string[];
  trigger: string[];
  type: Type[];
  frequency: Frequency[];
  transition: Transition[];
  operator: Operator[];
  conditions: Condition[];
};

type Platform = "app" | "web";
type Target = "free" | "premium";
type Type = "popup" | "push";
type Frequency = "once" | "every";
type Transition = "newTab" | "currentPage";
type Operator = "AND" | "OR";
type Condition = {
  fieldId: string;
  value: string[];
  min?: number;
  max?: number;
};
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";

.fade-enter-active {
  transition: opacity 0.3s;
}

.fade-leave-active {
  transition: opacity 0.1s;
}

.fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
  opacity: 0;
}

.dialog-mask {
  width: 100%;
  height: 100%;
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: -ms-flexbox;
  display: flex;
  z-index: 9000;
  -ms-flex-align: center;
  align-items: center;
  -ms-flex-pack: center;
  justify-content: center;
  background-color: var(--color-overlay);
}

.dialog-box {
  max-height: 80vh;
  max-width: 500px;
  box-sizing: border-box;
  border-radius: var(--radius-md);
  background-color: var(--color-background-normal);
  box-shadow: var(--shadow-md);
  color: var(--color-text-normal);

  .dialog-image {
    cursor: pointer;

    img {
      max-height: 80vh;
      max-width: 100%;
      object-fit: contain;
      border-radius: var(--radius-md);
      pointer-events: none;
    }
  }

  .dialog-header {
    img {
      max-height: 100%;
      max-width: 100%;
      object-fit: contain;
      border-radius: var(--radius-md) var(--radius-md) 0 0;
      pointer-events: none;
    }
  }

  .dialog-body {
    font-size: 1.1em;
    box-sizing: border-box;
    padding: 1.2em 2em 2em;

    h1 {
      text-align: center;
      margin: 0;
      font-size: 1.4em;
    }

    .text-container {
      margin-top: 20px;
      text-align: initial;
      white-space: pre-line;
    }
  }

  .button-area {
    border-bottom: none;
    display: flex;
    justify-content: space-around;

    .button {
      width: 100%;
      box-sizing: border-box;
      border: none;
      border-top: 1px solid var(--color-border);
      margin: 0;
      padding: 15px 20px;
      font-size: 1.1em;
      font-weight: var(--font-weight-bold);
      cursor: pointer;
      background-color: var(--color-background-normal);
      transition: background-color 0.2s ease;

      &.default {
        position: relative;
        @include hover-overlay;
        border-right: 1px solid var(--color-border);
        border-radius: 0 0 0 var(--radius-md);
        color: var(--color-text-low);

        &.single {
          border-radius: 0 0 var(--radius-md) var(--radius-md);
        }

      }

      &.primary {
        border-radius: 0 0 var(--radius-md) 0;
        color: var(--color-primary);

        &:hover {
          background-color: var(--color-primary);
          color: var(--color-text-on-fixed-bg);
        }
      }
    }
  }
}
</style>
